import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import Container from '@mui/material/Container';
import { Controller, useForm } from 'react-hook-form';
import Auth from '@aws-amplify/auth';
import CircularProgress from '@mui/material/CircularProgress';
import { emailValidation } from 'utils/regex';
import Logger from 'utils/logger';
import T from 'i18n';
import { useSelector } from 'react-redux';
import { selectQuery } from 'common/selectors';
import { Trans } from 'react-i18next';
import { ssoAuthService } from './ssoAuthService';

const useStyles = makeStyles({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: '1rem',
  },
  avatar: {
    backgroundColor: '#f50057 !important',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
  },
  magicText: {
    display: 'flex',
    padding: '1rem 2rem 1rem 1rem',
    backgroundColor: '#F3F3F3',
    borderRadius: '0.25rem',
  },
  emailDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    backgroundColor: '#F5F5F5',
    borderRadius: '0.25rem',
    marginBottom: '0.5rem',
    fontSize: '1rem',
  },
  // error: { height: '1rem' },
});

const SSO_ERROR_MESSAGES = {
  sso_failed: 'SSO login failed. Please try again or contact your IT admin.',
  account_deactivated: 'Your account has been deactivated. Contact your IT admin.',
};

function SignIn({
  onSignupClick,
  onTroubleSigningIn,
  onMfa,
  onRequirePhoneNumber,
  onSignInCompleted,
  onConfirmUser,
}) {
  const ssoEnabled = process.env.REACT_APP_SSO_ENABLED === 'true';
  const classes = useStyles();
  const queryParams = useSelector(selectQuery);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(!ssoEnabled);
  const [ssoRedirecting, setSsoRedirecting] = useState(false);

  const {
    formState: { errors },
    handleSubmit,
    control,
    getValues,
  } = useForm({ defaultValues: { email: queryParams.email } });

  // Handle SSO error params from callback redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorType = params.get('error');
    if (errorType && SSO_ERROR_MESSAGES[errorType]) {
      setMessage(SSO_ERROR_MESSAGES[errorType]);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleContinue = async (data) => {
    const { email } = data;

    // SSO disabled — skip domain check, go straight to password
    if (!ssoEnabled) {
      setShowPassword(true);
      return;
    }

    const domain = email.split('@')[1];

    if (!domain) {
      setMessage(T.translate('auth.emailInvalid'));
      return;
    }

    setLoading(true);
    setMessage(null);

    let redirecting = false;

    try {
      const result = await ssoAuthService.checkDomain(email);

      if (result.sso && result.nonce) {
        // SSO domain — redirect to corporate IdP using nonce
        redirecting = true;
        setSsoRedirecting(true);
        ssoAuthService.redirectToSso(result.nonce);
        return; // Keep loading while redirecting
      }

      // Not SSO — show password field for Cognito auth
      setShowPassword(true);
    } catch {
      // Fail open — show password field
      setShowPassword(true);
    } finally {
      if (!redirecting) {
        setLoading(false);
      }
    }
  };



  const signInPassword = (data) => {
    const { email, password } = data;

    if (!showPassword) {
      // Step 1: check domain first
      handleContinue(data);
      return;
    }

    // Step 2: password login via Cognito
    const resetPasswordLink = T.translate('auth.resetPasswordLink');

    // prettier-ignore
    const invalidCredentialsNoUserMessage = (
      <Trans i18nKey={'auth.invalidCredentialsNoUserMessage'}>
        Your email or password is incorrect. If you forgot your password, please <Link component="button" variant="h6" onClick={onTroubleSigningIn}>
          {{ resetPasswordLink }}
        </Link> now
      </Trans>
    );

    setLoading(true);
    Auth.signIn(email, password)
      .then((user) => {
        if (user.challengeName) {
          onMfa(user, password);
        } else if (user.attributes && user.attributes.phone_number) {
          onSignInCompleted();
        } else {
          onRequirePhoneNumber(user);
        }
      })
      .catch((e) => {
        Logger.ERROR(e);
        if (e.code === 'UserNotFoundException') {
          // prettier-ignore
          setMessage(
            invalidCredentialsNoUserMessage
          );
        } else if (e.code === 'InvalidParameterException') {
          setMessage(T.translate('auth.otherIssueMessage'));
        } else if (e.code === 'NotAuthorizedException') {
          setMessage(invalidCredentialsNoUserMessage);
        } else if (
          e.code === 'UserLambdaValidationException' &&
          e.name === 'UserLambdaValidationException'
        ) {
          onMfa({});
        } else if (e.code === 'UserNotConfirmedException') {
          onConfirmUser(email, true);
        } else {
          setMessage(T.translate('auth.otherIssueMessage'));
        }
      })
      .finally(() => setLoading(false));
  };

  const handleChangeEmail = () => {
    setShowPassword(false);
    setMessage(null);
  };

  // SSO redirecting state — show full-screen spinner
  if (ssoRedirecting) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper} style={{ marginTop: 0, paddingTop: '3rem' }}>
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Redirecting to your identity provider...
          </Typography>
        </div>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper} style={{ marginTop: 0 }}>
        <Avatar className={classes.avatar} sx={{ m: 1 }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {T.translate('auth.signIn')}
        </Typography>
        <Typography className={classes.error} color="error" component="h1" variant="h6">
          {message}
        </Typography>
        <form
          onSubmit={handleSubmit(signInPassword)}
          className={classes.form}
          style={{ marginTop: '24px' }}
          noValidate
        >
          {/* SSO enabled, step 1: email only + Continue */}
          {ssoEnabled && !showPassword && (
            <>
              <Controller
                name="email"
                rules={{ required: true, pattern: emailValidation }}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="email"
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label={T.translate('auth.email')}
                    autoComplete="email"
                    autoFocus
                    error={errors.email}
                    helperText={
                      errors.email &&
                      T.translate(
                        errors.email.type === 'required'
                          ? 'auth.emailRequired'
                          : 'auth.emailInvalid',
                      )
                    }
                    style={{ minHeight: '6rem', marginTop: 0 }}
                    InputLabelProps={{ style: { fontSize: '1.2rem', fontWeight: 500 } }}
                    InputProps={{ style: { fontSize: '1.2rem' } }}
                  />
                )}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mt: 3, mb: 2, fontSize: '1rem' }}
              >
                {loading && <CircularProgress size={17} />}
                {!loading && T.translate('auth.continue')}
              </Button>
            </>
          )}

          {/* SSO enabled, step 2: locked email + password */}
          {ssoEnabled && showPassword && (
            <>
              <div className={classes.emailDisplay}>
                <Typography variant="body1">{getValues('email')}</Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleChangeEmail}
                  sx={{ fontSize: '0.9rem' }}
                >
                  {T.translate('auth.change')}
                </Link>
              </div>

              <Controller
                name="password"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="password"
                    margin="normal"
                    required
                    fullWidth
                    label={T.translate('auth.password')}
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    error={errors.password}
                    helperText={errors.password && T.translate('auth.passwordRequired')}
                    style={{ minHeight: '6rem', marginTop: 0 }}
                    InputLabelProps={{ style: { fontSize: '1.2rem', fontWeight: 500 } }}
                    InputProps={{ style: { fontSize: '1.2rem' } }}
                  />
                )}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mt: 3, mb: 2, fontSize: '1rem' }}
              >
                {loading && <CircularProgress size={17} />}
                {!loading && T.translate('auth.signIn')}
              </Button>
              <Grid container>
                <Grid item xs>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={onTroubleSigningIn}
                    sx={{ fontSize: '1rem' }}
                  >
                    {T.translate('auth.forgotPasswordLink')}
                  </Link>
                </Grid>
                <Grid item>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={onSignupClick}
                    sx={{ fontSize: '1rem' }}
                  >
                    {T.translate('auth.dontHaveAnAccount')}
                  </Link>
                </Grid>
              </Grid>
            </>
          )}

          {/* SSO disabled: email + password together */}
          {!ssoEnabled && (
            <>
              <Controller
                name="email"
                rules={{ required: true, pattern: emailValidation }}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="email"
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label={T.translate('auth.email')}
                    autoComplete="email"
                    autoFocus
                    error={errors.email}
                    helperText={
                      errors.email &&
                      T.translate(
                        errors.email.type === 'required'
                          ? 'auth.emailRequired'
                          : 'auth.emailInvalid',
                      )
                    }
                    style={{ minHeight: '6rem', marginTop: 0 }}
                    InputLabelProps={{ style: { fontSize: '1.2rem', fontWeight: 500 } }}
                    InputProps={{ style: { fontSize: '1.2rem' } }}
                  />
                )}
              />
              <Controller
                name="password"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="password"
                    margin="normal"
                    required
                    fullWidth
                    label={T.translate('auth.password')}
                    type="password"
                    autoComplete="current-password"
                    error={errors.password}
                    helperText={errors.password && T.translate('auth.passwordRequired')}
                    style={{ minHeight: '6rem', marginTop: 0 }}
                    InputLabelProps={{ style: { fontSize: '1.2rem', fontWeight: 500 } }}
                    InputProps={{ style: { fontSize: '1.2rem' } }}
                  />
                )}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mt: 3, mb: 2, fontSize: '1rem' }}
              >
                {loading && <CircularProgress size={17} />}
                {!loading && T.translate('auth.signIn')}
              </Button>
              <Grid container>
                <Grid item xs>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={onTroubleSigningIn}
                    sx={{ fontSize: '1rem' }}
                  >
                    {T.translate('auth.forgotPasswordLink')}
                  </Link>
                </Grid>
                <Grid item>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={onSignupClick}
                    sx={{ fontSize: '1rem' }}
                  >
                    {T.translate('auth.dontHaveAnAccount')}
                  </Link>
                </Grid>
              </Grid>
            </>
          )}
        </form>
      </div>
    </Container>
  );
}

SignIn.propTypes = {};
SignIn.defaultProps = {};

export default SignIn;
